import { configureStore } from "@reduxjs/toolkit";
import rootReducer from "modules";
import { auditService } from "services/auditService";
import { authService } from "services/authService";
import { authServiceWithToken } from "services/authServiceWithToken";
import { countryService } from "services/countryService";
import { merchantService } from "services/merchantService";
import { notificationSettingService } from "services/notificationSettingService";
import { partnerService } from "services/partnerService";
import { paymentService } from "services/paymentService";
import { userService } from "services/userService";
import { smartRoutingService } from "services/smartRoutingService";
import { reportService } from "services/reportService";
import { payoutService } from "services/payoutService";
import { transactionService } from "services/transactionService";
import { billingService } from "services/billingService";
import { internalService } from "services/internalService";
import { infoService } from "services/infoService";
import { dashboardService } from "services/dashboardService";
import { webhookService } from "../../services/webhookService";
import { kycService } from "../../services/kycService";
import { onboardingSettingService } from "../../services/onboardingSettingService";
import { emailContentService } from "../../services/emailContentService";
import { emailRecipientsService } from "../../services/emailRecipientsService";
import { partnerWhitelabelConfigService } from "../../services/partnerWhitelabelConfigService";
import { transactionFeeService } from "../../services/transactionFeeService";
import { crossCheckService } from "services/crossCheckService";
import { PSPSettingService } from "../../services/pspManagementService";
import { virtualTerminalService } from "../../services/virtualTerminalService";
import { paymentInternalService } from "services/paymentInternalService";
import { receivableService } from "services/receivableService";
import { vaultService } from "services/vaultService";
import { paymentSettingService } from "services/paymentSettingService";
import { cardTypeSupportedService } from "services/cardTypeSupportedService";
import { adminPaymentService } from "services/adminPaymentService";
import { fileDownloadService } from "services/fileDownloadService";
import { urlManagementService } from "services/urlManagementService";
import { threeDsService } from "services/threeDsService";
import { transactionRefundRequestService } from "services/transactionRefundRequestService";
import { walletInternalTransferService } from "services/walletInternalTransferService";
import { adjustmentService } from "services/adjustmentService";

const store = configureStore({
  reducer: {
    [countryService.reducerPath]: countryService.reducer,
    [partnerService.reducerPath]: partnerService.reducer,
    [authService.reducerPath]: authService.reducer,
    [merchantService.reducerPath]: merchantService.reducer,
    [userService.reducerPath]: userService.reducer,
    [auditService.reducerPath]: auditService.reducer,
    [authServiceWithToken.reducerPath]: authServiceWithToken.reducer,
    [notificationSettingService.reducerPath]: notificationSettingService.reducer,
    [paymentService.reducerPath]: paymentService.reducer,
    [smartRoutingService.reducerPath]: smartRoutingService.reducer,
    [reportService.reducerPath]: reportService.reducer,
    [payoutService.reducerPath]: payoutService.reducer,
    [transactionService.reducerPath]: transactionService.reducer,
    [internalService.reducerPath]: internalService.reducer,
    [billingService.reducerPath]: billingService.reducer,
    [infoService.reducerPath]: infoService.reducer,
    [dashboardService.reducerPath]: dashboardService.reducer,
    [webhookService.reducerPath]: webhookService.reducer,
    [kycService.reducerPath]: kycService.reducer,
    [onboardingSettingService.reducerPath]: onboardingSettingService.reducer,
    [emailContentService.reducerPath]: emailContentService.reducer,
    [emailRecipientsService.reducerPath]: emailRecipientsService.reducer,
    [partnerWhitelabelConfigService.reducerPath]: partnerWhitelabelConfigService.reducer,
    [transactionFeeService.reducerPath]: transactionFeeService.reducer,
    [crossCheckService.reducerPath]: crossCheckService.reducer,
    [PSPSettingService.reducerPath]: PSPSettingService.reducer,
    [virtualTerminalService.reducerPath]: virtualTerminalService.reducer,
    [paymentInternalService.reducerPath]: paymentInternalService.reducer,
    [receivableService.reducerPath]: receivableService.reducer,
    [vaultService.reducerPath]: vaultService.reducer,
    [paymentSettingService.reducerPath]: paymentSettingService.reducer,
    [cardTypeSupportedService.reducerPath]: cardTypeSupportedService.reducer,
    [adminPaymentService.reducerPath]: adminPaymentService.reducer,
    [fileDownloadService.reducerPath]: fileDownloadService.reducer,
    [urlManagementService.reducerPath]: urlManagementService.reducer,
    [threeDsService.reducerPath]: threeDsService.reducer,
    [transactionRefundRequestService.reducerPath]: transactionRefundRequestService.reducer,
    [walletInternalTransferService.reducerPath]: walletInternalTransferService.reducer,
    [adjustmentService.reducerPath]: adjustmentService.reducer,
    rootReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      countryService.middleware,
      partnerService.middleware,
      authService.middleware,
      merchantService.middleware,
      userService.middleware,
      auditService.middleware,
      authServiceWithToken.middleware,
      notificationSettingService.middleware,
      paymentService.middleware,
      smartRoutingService.middleware,
      reportService.middleware,
      payoutService.middleware,
      transactionService.middleware,
      internalService.middleware,
      billingService.middleware,
      infoService.middleware,
      dashboardService.middleware,
      webhookService.middleware,
      kycService.middleware,
      onboardingSettingService.middleware,
      emailContentService.middleware,
      emailRecipientsService.middleware,
      partnerWhitelabelConfigService.middleware,
      transactionFeeService.middleware,
      crossCheckService.middleware,
      PSPSettingService.middleware,
      virtualTerminalService.middleware,
      paymentInternalService.middleware,
      receivableService.middleware,
      vaultService.middleware,
      fileDownloadService.middleware,
      urlManagementService.middleware,
      paymentSettingService.middleware,
      adminPaymentService.middleware,
      cardTypeSupportedService.middleware,
      threeDsService.middleware,
      transactionRefundRequestService.middleware,
      walletInternalTransferService.middleware,
      adjustmentService.middleware,
    ),
});

export default store;
export type RootState = ReturnType<typeof store.getState>;
